import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { handleSmartBack } from "../../../../utils/navigationHelper";
import classRoomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import { Form, Button, Card, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import "bootstrap/dist/css/bootstrap.min.css";
import "./CreateLesson.css";
import BackButton from "../../TeacherAdmin/Goback/GoBack";
import QuestSelector from "./QuestSelector";
import ClassroomSelector from "./ClassroomSelector";
import CustomInput from "../../../common/CustomInput";
import CustomTextArea from "../../../common/CustomTextArea";
import { useGetLessonDetail } from "../../../../hooks/teacher/lessons/useGetLessonDetail";
import { useUpdateLesson } from "../../../../hooks/teacher/lessons/useUpdateLesson";
import { useAssignQuestToLesson } from "../../../../hooks/teacher/lessons/useAssignQuestToLesson";
import { useAssignClassrooms } from "../../../../hooks/teacher/lessons/useAssignClassrooms";
import SuccessModal from "../../../common/SuccessModal";
import ScrollToError from "../../../../utils/ScrollToError";

const formatDateTime = (date) => {
  return date.includes(" ") ? date : `${date} 00:00:00`;
};

/**
 * @param {{ type: "Teacher" | "School" }} props
 */

const UpdateLesson = ({ type }) => {
  const navigate = useNavigate();
  const { id, teacherId } = useParams();
  const topRef = useRef(null);
  const lessonNameRef = useRef(null);
  const lessonDescriptionRef = useRef(null);

  // Fetch lesson data
  const {
    lessonDetail,
    loading: fetchLoading,
    error: fetchError,
    getLessonDetail,
  } = useGetLessonDetail();

  const [assignedClasses, setAssignedClasses] = useState([]);
  /**
   * Success message state
   * @typedef {Object} Message
   * @property {string} message - Success message
   * @property {string} description - Success description
   * @property {"success"|"error"} type - Success type
   */
  const [successMessage, setSuccessMessage] = useState(
    /** @type {Message} */({
      message: "",
      description: "",
      type: "success",
    }),
  );
  const [showModal, setShowModal] = useState(false);

  const {
    updateLesson,
    loading: updateLessonLoader,
    error: updateLessonError,
  } = useUpdateLesson();
  const {
    assignQuestToLesson,
    loading: assigningQuestLoader,
    error: assigningQuestError,
    setError: setAssigningQuestError,
  } = useAssignQuestToLesson();

  const {
    assignClassrooms,
    loading: assigningClassroomLoader,
    error: assigningClassroomError,
    setError: setAssigningClassroomError,
  } = useAssignClassrooms();

  // Fetch lesson details on mount
  useEffect(() => {
    if (id) {
      getLessonDetail({ lessonId: id });
    }
  }, [id]);

  // Pre-populate assigned classes when lesson data loads
  useEffect(() => {
    if (lessonDetail?.classrooms) {
      const existingClasses = lessonDetail.classrooms.map((cls) => ({
        id: cls.id,
        name: cls.name,
        dueDate: cls.pivot?.due_date?.split("T")[0],
      }));

      setAssignedClasses(existingClasses);
    }
  }, [lessonDetail]);

  const handleDueDateChange = (index, newDate) => {
    const updated = [...assignedClasses];
    updated[index].dueDate = newDate;
    setAssignedClasses(updated);
  };

  const removeClass = (index) => {
    setAssignedClasses(assignedClasses.filter((_, i) => i !== index));
  };

  const validationSchema = Yup.object({
    lessonName: Yup.string()
      .min(3, "Lesson name must be at least 3 characters long")
      .required("Lesson name is required")
      .max(100, "Lesson name must be at most 100 characters long")
      .matches(
        /^[A-Za-z0-9 _-]+$/,
        "Lesson name can only contain letters, numbers, spaces, underscores, and hyphens"
      ),
    lessonDescription: Yup.string()
      .min(10, "Description must be at least 10 characters long")
      .required("Lesson Description is required")

      .max(1000, "Description must be at most 1000 characters long"),
    selectedQuests: Yup.array().min(1, "Select at least one Quest"),
  });

  /**
   * Get initial values from fetched lesson data
   */
  const getInitialValues = () => {
    if (!lessonDetail) {
      return {
        lessonName: "",
        lessonDescription: "",
        selectedQuests: [],
      };
    }

    return {
      lessonName: lessonDetail.title || "",
      lessonDescription: lessonDetail.description || "",
      selectedQuests: (lessonDetail.quests || []).map((quest) => ({
        id: quest.id,
        title: quest.title,
        thumbnail: quest.thumbnail,
        description: quest.description,
      })),
    };
  };

  const handleSubmit = async (values) => {
    setSuccessMessage({
      message: "",
      description: "",
      type: "success",
    });
    setAssigningClassroomError(null);
    setAssigningQuestError(null);

    // previous quests
    const previousQuestIds = (lessonDetail?.quests || []).map((q) => q.id);

    // currently selected quests from form values
    const selectedQuestIds = values.selectedQuests.map((q) => q.id);

    const addedQuests = selectedQuestIds
      .filter((id) => !previousQuestIds.includes(id))
      .map((id) => ({
        id,
        is_assign: true,
      }));

    // 4. Removed quests (assign = false)
    const removedQuests = previousQuestIds
      .filter((id) => !selectedQuestIds.includes(id))
      .map((id) => ({
        id,
        is_assign: false,
      }));

    // 5. Final payload
    const updatedQuests = [...addedQuests, ...removedQuests];

    const previousClasses = lessonDetail.classrooms || [];

    const deletedClasses = previousClasses
      .filter(
        (prevData) => !assignedClasses.some((cls) => cls.id === prevData.id),
      )
      .map((data) => ({
        id: data.id,
        is_assign: false,
      }));
    const addedClasses = assignedClasses.map((data) => {
      return {
        id: data.id,
        is_assign: true,
        due_date: formatDateTime(data.dueDate),
      };
    });

    const updatedClasses = [...deletedClasses, ...addedClasses];
    const response = await updateLesson({
      lessonId: id,
      lessonData: {
        title: values.lessonName,
        description: values.lessonDescription,
      },
    });

    if (response) {
      if (updatedQuests.length > 0) {
        const assignQuestsResponse = await assignQuestToLesson({
          lessonId: id,
          questsData: updatedQuests,
        });

        if (!assignQuestsResponse) {
          topRef.current?.scrollIntoView({ behavior: "smooth" });
          return;
        }
      }

      if (updatedClasses.length > 0) {
        const assignClassroomsResponse = await assignClassrooms({
          lessonId: id,
          assignmentData: updatedClasses,
        });

        if (!assignClassroomsResponse) {
          topRef.current?.scrollIntoView({ behavior: "smooth" });
          return;
        }
      }
      setSuccessMessage({
        message: "Lesson details updated successfully",
        description: "Lesson details updated successfully",
        type: "success",
      });
      setShowModal(true);
      topRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Loading state
  if (fetchLoading) {
    return (
      <div className="">
        <BackButton />
        <div className="classroom-container">
          <img src={classRoomImg} className="img" alt="Classroom" />
          <h2 className="text">Update Lesson</h2>
        </div>
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading lesson details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="">
        <BackButton />
        <div className="classroom-container">
          <img src={classRoomImg} className="img" alt="Classroom" />
          <h2 className="text">Update Lesson</h2>
        </div>
        <Card className="mt-4 p-4 shadow-sm border-0">
          <div className="d-flex justify-content-center py-5">
            <div className="text-center" style={{ maxWidth: "400px" }}>
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(220, 53, 69, 0.1)",
                }}
              >
                <i
                  className="bi bi-exclamation-triangle"
                  style={{ fontSize: "28px", color: "#dc3545" }}
                ></i>
              </div>
              <h5 className="mb-2" style={{ color: "#333", fontWeight: "600" }}>
                Unable to Load Lesson
              </h5>
              <p className="text-muted mb-4" style={{ fontSize: "14px" }}>
                {fetchError}
              </p>
              <button
                className="btn btn-outline-primary btn-sm px-4"
                onClick={() => getLessonDetail({ lessonId: id })}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Try Again
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Not found state
  if (!lessonDetail) {
    return (
      <div className="">
        <BackButton />
        <div className="classroom-container">
          <img src={classRoomImg} className="img" alt="Classroom" />
          <h2 className="text">Update Lesson</h2>
        </div>
        <Card className="mt-4 p-4 shadow-sm border-0">
          <div className="d-flex justify-content-center py-5">
            <div className="text-center" style={{ maxWidth: "400px" }}>
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255, 193, 7, 0.15)",
                }}
              >
                <i
                  className="bi bi-folder-x"
                  style={{ fontSize: "28px", color: "#f59e0b" }}
                ></i>
              </div>
              <h5 className="mb-2" style={{ color: "#333", fontWeight: "600" }}>
                Lesson Not Found
              </h5>
              <p className="text-muted mb-4" style={{ fontSize: "14px" }}>
                The lesson you're looking for doesn't exist or may have been
                deleted.
              </p>
              <button
                className="btn btn-outline-secondary btn-sm px-4"
                onClick={() => handleSmartBack(navigate, type === "School" ? "/school/lesson" : "/teacher/lesson")}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Go Back
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="">
      <div className="">
        <BackButton />
      </div>
      <div className="classroom-container" ref={topRef}>
        <img src={classRoomImg} className="img" alt="Classroom" />
        <h2 className="text">Update Lesson</h2>
      </div>

      <Card className="mt-4 p-4 shadow-sm border-0">
        <div>
          {/* Error Message */}
          {(updateLessonError ||
            assigningQuestError ||
            assigningClassroomError) && (
              <div className="alert alert-danger mt-3" role="alert">
                <i className="bi bi-exclamation-circle-fill me-2"></i>
                {updateLessonError
                  ? updateLessonError
                  : assigningQuestError
                    ? assigningQuestError
                    : assigningClassroomError}
              </div>
            )}
        </div>

        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ handleSubmit, isSubmitting }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <ScrollToError
                  fieldRefs={{
                    lessonName: lessonNameRef,
                    lessonDescription: lessonDescriptionRef,
                  }}
                />
                {/* LESSON NAME */}
                <div className="mb-3">
                  <CustomInput
                    type="text"
                    name="lessonName"
                    label="Lesson Name"
                    placeholder="Lesson Name"
                    autoComplete="off"
                    layout="stacked"
                    ref={lessonNameRef}
                  />
                </div>

                {/* LESSON DESCRIPTION */}
                <div className="mb-3">
                  <CustomTextArea
                    type="text"
                    name="lessonDescription"
                    label="Lesson Description"
                    placeholder="Lesson Description"
                    autoComplete="off"
                    layout="stacked"
                    ref={lessonDescriptionRef}
                  />
                </div>

                {/* QUESTS SELECTION (Dynamic Tables) */}
                <div className="mt-4">
                  <QuestSelector name="selectedQuests" />
                </div>

                {/* ASSIGN LESSON SECTION */}
                <div className="mt-5">
                  <ClassroomSelector
                    assignedClasses={assignedClasses}
                    setAssignedClasses={setAssignedClasses}
                    teacherId={teacherId || null}
                    type={type}
                  />
                </div>

                {/* ASSIGNED CLASSES LIST */}
                {assignedClasses.length > 0 && (
                  <div className="mt-4">
                    <h5 className="fw-semibold">Assigned Class</h5>
                    <div className="bg-light p-3 rounded">
                      {assignedClasses.map((cls, index) => {
                        return (
                          <div
                            key={index}
                            className="d-flex flex-column flex-md-row justify-content-between align-items-md-center bg-red p-3 mb-2 rounded shadow-sm assigned-classes gap-2"
                          >
                            {/* CLASS NAME */}
                            <span
                              className="mb-2 mb-md-0 flex-grow-1"
                              style={{ wordBreak: "break-word" }}
                            >
                              {cls.name}
                            </span>

                            {/* DATE + REMOVE BUTTON */}
                            <div className="d-flex align-items-center gap-3 flex-shrink-0 mt-2 mt-md-0">
                              {/* <div className="d-flex md:flex-row align-items-center flex-wrap"> */}
                              <div className="d-flex flex-row align-items-center flex-wrap">
                                <span className="small me-2 text-nowrap fw-semibold text-dark">
                                  Pick a Due Date :
                                </span>
                                <Form.Control
                                  type="date"
                                  value={
                                    cls.dueDate
                                      ? cls.dueDate.substring(0, 10)
                                      : ""
                                  }
                                  onFocus={(e) => {
                                    // Only restrict when user opens the picker
                                    e.target.min = new Date()
                                      .toISOString()
                                      .split("T")[0];
                                  }}
                                  onBlur={(e) => {
                                    // Remove min restriction after user closes the picker
                                    e.target.min = "";
                                  }}
                                  onChange={(e) =>
                                    handleDueDateChange(index, e.target.value)
                                  }
                                  size="sm"
                                  style={{
                                    width: "140px",
                                    border: "2px solid #014782",
                                    borderRadius: "6px",
                                  }}
                                />
                              </div>

                              <Button
                                variant="light"
                                className="p-0 border-0 "
                                onClick={() => removeClass(index)}
                                style={{
                                  background: "transparent",
                                }}
                              >
                                <i className="bi bi-x-lg"></i>
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* SUBMIT BUTTON */}
                <div className="mt-5">
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      updateLessonLoader ||
                      assigningQuestLoader ||
                      assigningClassroomLoader
                    }
                    className="custom-primary-button"
                  >
                    {isSubmitting ||
                      updateLessonLoader ||
                      assigningClassroomLoader ||
                      assigningQuestLoader
                      ? "Updating Lesson..."
                      : "Update Lesson"}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Card>
      <SuccessModal
        type={successMessage.type}
        showModal={showModal}
        handleClose={setShowModal}
        title={successMessage.message}
        message={successMessage.description}
        primaryButtonText="Continue"
        secondaryButtonText="Lessons List"
        secondaryButtonLink={"/school-admin/lesson"}
      />
    </div>
  );
};

export default UpdateLesson;
